import { RefObject, useState } from 'react';
import { MdPerson } from 'react-icons/md';
import { useLocation } from 'react-router';
import { Icon, Modal, useToastNotifications } from '@nbirl/nbi-common-ui-libs';

import { ShareLink } from '../../../../../../shared/components';
import { useCurrentUser } from '../../../../../../shared/context';
import { useErrorToast } from '../../../../../../shared/hooks';
import { EmailPurpose } from '../../../models';
import { useShareDocumentWithUserMutation } from '../../../queries';
import {
  ShareDocumentForm,
  ShareDocumentFormValues,
} from './share-document-form';

export type ShareModalProps = {
  documentId: string;
  folderId: string;
  isPending?: boolean;
  onDismiss: () => void;
};

export const ShareDocumentModal = ({
  documentId,
  folderId,
  isPending,
  onDismiss,
}: ShareModalProps) => {
  const location = useLocation();
  const currentUser = useCurrentUser();
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const [sendLinkButtonRef, setSendLinkButtonRef] =
    useState<RefObject<HTMLButtonElement>>();
  const shareDocumentWithUserMutation = useShareDocumentWithUserMutation();
  const params = new URLSearchParams({ documentId, userId: currentUser.id });
  const link = `${window.location.origin}${
    location.pathname
  }?${params.toString()}`;

  const handleSubmit = (values: ShareDocumentFormValues) => {
    shareDocumentWithUserMutation.mutate(
      {
        documentId,
        emailPurpose: values.subject as EmailPurpose,
        optionalMessage: values.message,
        recipients: values.to.map((r) => r.value),
        ccMyself: values.sendCopy,
      },
      {
        onSuccess: (response) => {
          if (
            response.unverifiedEmails &&
            response.unverifiedEmails.length > 0
          ) {
            addToast({
              autoRemove: false,
              variant: 'warning',
              description: `The document you shared was not sent to the following recipients: ${response.unverifiedEmails.join(
                ', '
              )}, as their profiles are not yet active.`,
              text: 'Document not sent to all recipients.',
            });
          } else {
            addToast({
              variant: 'success',
              text: 'Document successfully shared.',
              duration: 2000,
            });
            onDismiss();
          }
        },
        onError: () => {
          showErrorToast('The document could not be shared. Please try again.');
        },
      }
    );
  };

  return (
    <Modal
      heading="Share link via email"
      headingIcon={<Icon icon={<MdPerson />} />}
      isOpen={true}
      initialFocusRef={sendLinkButtonRef}
      onDismiss={onDismiss}
    >
      <ShareDocumentForm
        documentId={documentId}
        folderId={folderId}
        isPending={isPending}
        isSubmitting={shareDocumentWithUserMutation.isLoading}
        onPassButtonRef={(ref) => setSendLinkButtonRef(ref)}
        onSubmit={handleSubmit}
      />

      <ShareLink link={link} compact />
    </Modal>
  );
};
